<!-- src/components/EnhancedTeacherCard.vue -->
<template>
  <div class="card card-hover" data-testid="teacher-card">
    <div class="p-6">
      <div class="flex items-center">
        <div class="avatar avatar-lg avatar-hover">
          <img
            class="w-full h-full object-cover"
            :src="avatarSrc(teacher.photoURL)"
            alt="Teacher profile"
            @error="onAvatarError"
          />
        </div>
        <div class="ml-4 flex-1">
          <h3
            class="text-lg font-medium text-gray-900"
            data-testid="teacher-name"
          >
            {{ teacher.name }}
          </h3>
          <p class="text-sm text-gray-500">
            {{ formatInstrument(teacher.instrument) }}
          </p>

          <!-- Promotional Badge -->
          <div v-if="hasFirstLessonPromotion" class="mt-2">
            <promotional-badge
              type="first-lesson"
              variant="success"
              size="sm"
              :discount-percentage="getPromotionDiscount"
              data-testid="teacher-promotional-badge"
            />
          </div>
        </div>
      </div>

      <div class="mt-4">
        <div class="flex items-center">
          <div class="flex items-center">
            <template v-for="n in 5" :key="n">
              <svg
                :class="[
                  n <= teacher.rating ? 'text-yellow-400' : 'text-gray-200',
                  'h-4 w-4 transition-colors duration-300',
                ]"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                />
              </svg>
            </template>
            <p class="ml-2 text-xs text-gray-500" data-testid="teacher-rating">
              {{ teacher.rating }} ({{ teacher.reviewCount || 0 }})
            </p>
          </div>
        </div>

        <p class="mt-3 text-sm text-gray-600 line-clamp-2">
          {{ teacher.bio.substring(0, 100)
          }}{{ teacher.bio.length > 100 ? "..." : "" }}
        </p>

        <div class="mt-4 flex flex-wrap">
          <span class="badge-green mr-2" data-testid="teacher-rate">
            £{{ teacher.hourlyRate }}/hour
          </span>
          <span
            v-for="(tag, index) in teacher.tags.slice(0, 3)"
            :key="index"
            class="badge-blue mr-2 mb-2"
          >
            {{ tag }}
          </span>
          <span v-if="teacher.tags.length > 3" class="badge-gray mb-2">
            +{{ teacher.tags.length - 3 }}
          </span>
        </div>
      </div>

      <div class="mt-5 space-y-2">
        <router-link
          :to="`/teachers/${teacher.id}`"
          class="btn-primary w-full group"
          data-testid="view-profile-btn"
        >
          <span class="group-hover:pr-2 transition-all duration-300"
            >Book Lesson</span
          >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 ml-2 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </router-link>

        <router-link
          :to="`/teachers/${teacher.id}`"
          class="btn-secondary w-full text-center block"
        >
          View Profile
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import PromotionalBadge from "./search/PromotionalBadge.vue";
import { avatarSrc, onAvatarError } from "@/utils/avatar";
import { formatInstrument } from "@/utils/instrument";

export default {
  name: "TeacherCard",
  components: {
    PromotionalBadge,
  },
  props: {
    teacher: {
      type: Object,
      required: true,
      validator: (teacher) => {
        return (
          teacher.id && teacher.name && teacher.instrument && teacher.hourlyRate
        );
      },
    },
  },
  setup(props, { emit }) {
    const router = useRouter();
    const isFavorite = ref(false);

    // Promotional logic
    const hasFirstLessonPromotion = computed(() => {
      return props.teacher.firstLessonSettings?.enabled === true;
    });

    const getPromotionDiscount = computed(() => {
      // Default to 50% if no specific discount is set
      const discount = props.teacher.firstLessonSettings?.customDiscount;
      return discount || 50;
    });

    // Toggle favorite status
    const toggleFavorite = () => {
      isFavorite.value = !isFavorite.value;
      emit("toggle-favorite", {
        teacherId: props.teacher.id,
        isFavorite: isFavorite.value,
      });
    };

    // Book a lesson
    const bookLesson = () => {
      router.push(`/teachers/${props.teacher.id}?book=true`);
    };

    return {
      isFavorite,
      toggleFavorite,
      bookLesson,
      hasFirstLessonPromotion,
      getPromotionDiscount,
      avatarSrc,
      onAvatarError,
      formatInstrument,
    };
  },
};
</script>
